import { getAuth } from '../../modules/auth';
import { handleAuthError } from '../../modules/auth/session';
import { ID, Response, createPayload } from '../../../_metronic/helpers';
import { Bookie, Category, Country, Market, Participant, Sport, TipsterType, User, Event, Media, Advertisement, Subscription, HomePromoCard, Article, HomeEvent} from '@apuestes/types';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Importar tipos del dashboard
import {
  SubscriptionResponse,
  KPIsResponse,
  Transaction,
  MonthlyBillingResponse,
  MonthlyActiveSubscriptionsResponse,
  ExpiringSubscriptionsResponse,
  ReportData,
  ListModelResponse
} from '../types/dashboard.types';
import { BackofficeUserDevice } from '../types/devices.types';

type models = Bookie | Category | Country | Market | Participant | Sport | TipsterType | User | Event | Media | Advertisement | Subscription | HomePromoCard | Article | HomeEvent

const API_URL = process.env.REACT_APP_API_URL
const RUNNERS_API_URL = process.env.REACT_APP_RUNNERS_API_URL

const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: async (headers) => {
        const token = getAuth()?.token

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    }
});

const runnersBaseQuery = fetchBaseQuery({
    baseUrl: RUNNERS_API_URL || '',
    prepareHeaders: async (headers) => {
        const token = getAuth()?.token

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    }
});

const baseQueryWithInterceptor: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    handleAuthError(result, args);
    return result;
};

const runnersBaseQueryWithInterceptor: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await runnersBaseQuery(args, api, extraOptions);
    handleAuthError(result, args);
    return result;
};

// Define a service using a base URL and expected endpoints
export const apuestesAPI = createApi({
    reducerPath: 'apuestesApi',
    baseQuery: baseQueryWithInterceptor,
    endpoints: (builder) => ({
        ///Generic Endpoints
        listModel: builder.query<ListModelResponse<any>, { model: string, query: string | null, includes?: string | null }>({
            query: ({ model, query, includes }) => {
                const searchParams = new URLSearchParams(query ?? undefined)
                const { newQuery } = createPayload(searchParams, ['post']);
                const includeQuery = includes ? `&include=${includes}` : '';
                return `${model}?${newQuery()}${includeQuery}`;
            },
        }),
        getModelById: builder.query<models, { model: string, id: ID, includes?: string }>({
            query: ({ model, id, includes }) => {
                const includeQuery = includes ? `?include=${includes}` : '';
                return `${model}/${id}${includeQuery}`;
            },
        }),
        getReportsByModel: builder.query<models, { model: string }>({
            query: ({ model }) => `${model}`,
        }),
        filterModel: builder.query<Response<Array<any>>, { model: string, query: string, includes: string }>({
            query: ({ model, query, includes }) => {
                const params = new URLSearchParams(query)
                const { payload, newQuery } = createPayload(params, ['post'])
                const body = payload()
                return {
                    url: `${model}/filter?${newQuery()}${includes && `&include=${includes}`}`,
                    method: 'POST',
                    body
                }
            },
        }),
        setModel: builder.mutation<models, { model: string, payload: any }>({
            query: ({ model, payload }) => ({
                url: `${model}`,
                method: 'POST',
                body: payload
            }),
        }),
        setMedia: builder.mutation<models, { model: string, id: ID, payload: FormData }>({
            query: ({ model, id, payload }) => ({
                url: `${model}/${id}/upload`,
                method: 'POST',
                body: payload
            })
        }),
        updateModel: builder.mutation<models, { model: string, id: ID, payload: any }>({
            query: ({ model, id, payload }) => ({
                url: `${model}/${id}`,
                method: 'PATCH',
                body: payload
            }),
        }),
        deleteModel: builder.mutation<models, { model: string, id: ID }>({
            query: ({ model, id }) => ({
                url: `${model}/${id}`,
                method: 'DELETE'
            }),
        }),
        toggleModel: builder.mutation<models, { model: string, id: ID, enabled: boolean }>({
            query: ({ model, id, enabled }) => ({
                url: `${model}/${id}?is=${enabled}`,
                method: 'PATCH'
            }),
        }),
        extendSubscription: builder.mutation<models, { payload: { subscription_id: number, ends_at: string } }>({
            query: ({ payload }) => ({
                url: `subscriptions/bo/extend`,
                method: 'POST',
                body: payload
            }),
        }),
        createSubscriptionBo: builder.mutation<
            models,
            { payload: { plan_id: number | null; user_id: number | null; starts_at: string; ends_at: string } }
        >({
            query: ({ payload }) => ({
                url: `subscriptions/bo`,
                method: 'POST',
                body: payload,
            }),
        }),
        approveSolicitation: builder.mutation<models, { id: ID, payload: { comment: string } }>({
            query: ({ id, payload }) => ({
                url: `request-tipster-types/bo/${id}/approve`,
                method: 'POST',
                body: payload
            }),
        }),
        rejectSolicitation: builder.mutation<models, { id: ID, payload: { comment: string } }>({
            query: ({ id, payload }) => ({
                url: `request-tipster-types/bo/${id}/reject`,
                method: 'POST',
                body: payload
            }),
        }),
        changeServiceTipsterType: builder.mutation<models, { serviceId: ID, tipsterTypeId: ID }>({
            query: ({ serviceId, tipsterTypeId }) => ({
                url: `services/bo/${serviceId}/convert-tipster-type`,
                method: 'PATCH',
                body: { tipster_type_id: tipsterTypeId }
            }),
        }),
        // Backoffice Devices Endpoints (Task 6/10: anomaly review de sesiones/devices)
        getBackofficeUserDevices: builder.query<BackofficeUserDevice[], { userId: ID }>({
            query: ({ userId }) => `backoffice/users/${userId}/devices`,
        }),
        revokeBackofficeUserDevice: builder.mutation<{ success: true }, { userId: ID, deviceId: string }>({
            query: ({ userId, deviceId }) => ({
                url: `backoffice/users/${userId}/devices/${deviceId}`,
                method: 'DELETE'
            }),
        }),
        // Dashboard Reports Endpoints
        getRecentTransactions: builder.query<Transaction[], void>({
            query: () => `reports/bo/transactions`,
        }),
        getDashboardKPIs: builder.query<KPIsResponse, void>({
            query: () => 'reports/bo/kpis',
        }),
        getSubscription: builder.query<SubscriptionResponse, void>({
            query: () => 'reports/bo/subscriptions',
        }),
        getReportData: builder.query<ReportData, { reportType: string; isPremium?: boolean }>({
            query: ({ reportType, isPremium = true }) => `reports/bo/${reportType}?isPremium=${isPremium}`,
        }),
        getMonthlyBilling: builder.query<MonthlyBillingResponse, void>({
            query: () => 'reports/bo/monthly-billing',
        }),
        getMonthlyNewCustomers: builder.query<any, void>({
            query: () => 'reports/bo/monthly-new-customers',
        }),
        getMonthlyActiveSubscriptions: builder.query<MonthlyActiveSubscriptionsResponse, void>({
            query: () => 'reports/bo/monthly-active-subscriptions',
        }),
        getExpiringSubscriptions: builder.query<ExpiringSubscriptionsResponse, void>({
            query: () => 'reports/bo/expiring-subscriptions',
        }),
        // Runners Endpoints
        getRunners: builder.query<any[], void>({
            queryFn: async (arg, api, extraOptions) => {
                if (!RUNNERS_API_URL) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'REACT_APP_RUNNERS_API_URL not configured' } } as any
                }
                const result = await runnersBaseQueryWithInterceptor({ url: '/runners' }, api, extraOptions)
                if (result.error) {
                    return { error: result.error } as any
                }
                return { data: (result.data as any) || [] } as any
            },
        }),
        toggleRunner: builder.mutation<any, { name: string; is: boolean }>({
            queryFn: async ({ name, is }, api, extraOptions) => {
                if (!RUNNERS_API_URL) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'REACT_APP_RUNNERS_API_URL not configured' } } as any
                }
                const result = await runnersBaseQueryWithInterceptor({ 
                    url: `/runners/toggle/${name}?is=${is}`,
                    method: 'PATCH'
                }, api, extraOptions)
                
                // Si hay un error de parsing pero el status original es 200, considerarlo éxito
                // El endpoint devuelve texto plano en lugar de JSON
                if (result.error && result.error.status === 'PARSING_ERROR' && result.error.originalStatus === 200) {
                    // El mensaje de éxito está en result.error.data
                    const successMessage = result.error.data || 'Operation successful'
                    return { 
                        data: { 
                            success: true, 
                            message: successMessage 
                        } 
                    } as any
                }
                
                // Si hay otro tipo de error, devolverlo
                if (result.error) {
                    return { error: result.error } as any
                }
                
                // Si la respuesta es exitosa (200), devolver los datos
                const responseData = result.data !== undefined && result.data !== null && result.data !== '' 
                    ? result.data 
                    : { success: true }
                
                return { data: responseData } as any
            },
        }),
    }),
    keepUnusedDataFor: 0,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useListModelQuery,
    useGetModelByIdQuery,
    useGetReportsByModelQuery,
    useLazyGetReportsByModelQuery,
    useLazyGetModelByIdQuery,
    useSetModelMutation,
    useUpdateModelMutation,
    useDeleteModelMutation,
    useToggleModelMutation,
    useFilterModelQuery,
    useSetMediaMutation,
    useExtendSubscriptionMutation,
    useCreateSubscriptionBoMutation,
    useApproveSolicitationMutation,
    useRejectSolicitationMutation,
    useChangeServiceTipsterTypeMutation,
    useGetBackofficeUserDevicesQuery,
    useRevokeBackofficeUserDeviceMutation,
    // Dashboard Reports Hooks
    useGetDashboardKPIsQuery,
    useGetRecentTransactionsQuery,
    useGetSubscriptionQuery,
    useGetMonthlyBillingQuery,
    useGetMonthlyNewCustomersQuery,
    useGetMonthlyActiveSubscriptionsQuery,
    useGetExpiringSubscriptionsQuery,
    // Generic Report Data Hook
    useGetReportDataQuery,
    // Runners Hooks
    useGetRunnersQuery,
    useToggleRunnerMutation
} = apuestesAPI

// Re-export dashboard types for convenience
export type {
    SubscriptionUser,
    SubscriptionResponse,
    KPIData,
    KPIsResponse,
    Transaction,
    TransactionsResponse,
    BillingData,
    MonthlyBillingResponse,
    MonthlyActiveSubscriptionData,
    MonthlyActiveSubscriptionsResponse,
    ExpiringSubscriptionData,
    ExpiringSubscriptionsResponse,
    ReportData,
    DashboardUser,
    ListModelResponse
} from '../types/dashboard.types'